import { FC, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { FaCheck, FaTimes, FaTrash } from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientUrl } from 'utils/helpers/client';
import { getSubdomain } from 'utils/helpers/routing';

import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import FetchTable from 'components/table/FetchTable';
import { buildFilters } from 'components/table/utils/filtersConfig';

import InnerWrapper from '../InnerWrapper';

const HourlyAbsencesScreen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();

  const isOperator = getSubdomain() === 'operators';
  const queryClient = useQueryClient();
  const history = useHistory();

  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({});

  const createHourlyAbsences = () => {
    const url = buildClientUrl('/hourly-absences/create', client);
    history.push(url);
  };

  const getTooltipText = (createdAt: string, updatedAt: string): string => {
    const createdDate = formatDate(new Date(createdAt), 'dd.mm.YYY hh:ii');
    const updatedDate = formatDate(new Date(updatedAt), 'dd.mm.YYY hh:ii');

    if (createdAt === updatedAt) {
      return `${t('hourlyAbsences.createdAt')}: ${createdDate}`;
    }

    return `${t('hourlyAbsences.createdAt')}: ${createdDate}\n${t('hourlyAbsences.updatedAt')}: ${updatedDate}`;
  };

  const {
    data: dropdowns,
    isLoading,
    error: dropdownError,
  } = useQuery(
    ['hourly-absences-dropdowns', client],
    (): Promise<{ [key: string]: any[] }> => {
      return API.get('hourly-absences/dropdown-options').then(res => {
        return res.data?.[language || 'bg'];
      });
    },
    { cacheTime: 0 }
  );

  const tableFilters = useMemo(
    () => [
      buildFilters({
        name: 'date_from',
        placeholder: t('hourlyAbsences.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'date_to',
        placeholder: t('hourlyAbsences.dateTo'),
        type: 'monthyearpickerend',
      }),
      buildFilters({
        name: 'status',
        placeholder: t('hourlyAbsences.status'),
        options: dropdowns?.status,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('hourlyAbsences.employee'),
        options: dropdowns?.user,
        isMulti: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'group',
        placeholder: t('hourlyAbsences.group'),
        options: dropdowns?.group,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ],
    [dropdowns]
  );

  const renderActions = (buttons: any[], id: number) => {
    if (!Array.isArray(buttons) || buttons.length === 0) return null;

    const handleAction = async (type: string, actionLink: string) => {
      showModal({
        type: 'confirm',
        heading: t('common.confirm'),
        content: t(`hourlyAbsences.confirmAbsence.${type}`),
        onConfirm: async () => {
          setLoadingState(prev => ({ ...prev, [`${id}-${type}`]: true }));
          try {
            if (type === 'delete') {
              await API.delete(actionLink);
            } else {
              await API.post(actionLink);
            }
            queryClient.invalidateQueries('hourly-absences');
          } catch (error) {
            showModal({
              type: 'error',
              content: createErrorFromResponse(error).message,
            });
          } finally {
            setLoadingState(prev => ({ ...prev, [`${id}-${type}`]: false }));
          }
        },
      });
    };

    return buttons.map(({ type, text, action_link: actionLink }) => {
      const isButtonLoading = loadingState[`${id}-${type}`];

      const getButtonIcon = () => {
        if (type === 'approve') return <FaCheck size={14} />;
        if (type === 'reject') return <FaTimes size={14} />;
        if (type === 'delete') return <FaTrash size={14} />;
      };

      const getButtonType = () => {
        if (type === 'approve') return 'success';
        if (type === 'reject') return 'danger';
        if (type === 'delete') return 'danger';
      };

      return (
        <div
          key={`${id}-${type}`}
          className="d-inline-block ml-2 mb-2"
        >
          <TooltipWrapper
            id={`tooltip-${type}-${id}`}
            overlay={text}
          >
            <Button
              type="button"
              btnType={getButtonType()}
              size="sm"
              loading={isButtonLoading}
              disabled={isButtonLoading}
              onClick={() => handleAction(type, actionLink)}
              outlined={type === 'delete'}
            >
              {getButtonIcon()}
            </Button>
          </TooltipWrapper>
        </div>
      );
    });
  };

  const columns: Array<Column<any>> = [
    {
      Header: t('hourlyAbsences.name'),
      accessor: 'user',
      Cell: ({ row }) => {
        const { first_name: firstName, middle_name: middleName, last_name: lastName } = row.original.user;
        const { created_at: createdAt, updated_at: updatedAt } = row.original;
        const tooltip = getTooltipText(createdAt, updatedAt);
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={tooltip}
          >
            {firstName} {middleName || ''} {lastName}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('hourlyAbsences.group'),
      accessor: 'user.groups',
      Cell: ({ row }) => {
        const { groups }: { groups: { id: number; name: string }[] } = row.original.user;
        return <span>{groups && groups.length > 0 ? groups.map(group => group.name).join(', ') : ''}</span>;
      },
    },
    {
      Header: t('hourlyAbsences.date'),
      width: '20%',
      accessor: 'date_from',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { date_from: dateFrom, date_to: dateTo, created_at: createdAt, updated_at: updatedAt } = row.original;
        const formattedFrom = formatDate(new Date(dateFrom), 'dd.mm.yyy hh:ii');
        const formattedTo = formatDate(new Date(dateTo), 'hh:ii');
        const tooltip = getTooltipText(createdAt, updatedAt);
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={tooltip}
          >
            {formattedFrom} - {formattedTo}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('hourlyAbsences.hours'),
      accessor: 'hours',
      width: '10%',
      Cell: ({ cell }) => {
        const hours = Number(cell.value);
        const formattedHours = Number.isInteger(hours) ? hours : hours.toFixed(1);
        return <span>{formattedHours}</span>;
      },
    },
    {
      Header: t('hourlyAbsences.status'),
      accessor: 'status',
      width: '10%',
      Cell: ({ row }) => {
        const { status, created_at: createdAt, updated_at: updatedAt } = row.original;
        const tooltip = getTooltipText(createdAt, updatedAt);
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={tooltip}
          >
            {t(`hourlyAbsences.statuses.${status}`)}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('hourlyAbsences.leaveApplied'),
      accessor: 'hours_covered_total',
      Cell: ({ row }) => {
        const hoursRequested = Number(row.original.hours) || 0;
        const hoursCoveredTotal = Number(row.original.hours_covered_total) || 0;
        const hoursCovered = row.original.hours_covered;

        const formatNumber = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

        if (hoursCoveredTotal <= 0) {
          return <span>{t('hourlyAbsences.no')}</span>;
        }

        const dateFormatted =
          hoursCovered && hoursCovered.length > 0
            ? hoursCovered.map((hc: { date_formatted: string }) => hc.date_formatted).join(' / ')
            : '';

        if (hoursRequested > 0 && hoursCoveredTotal < hoursRequested) {
          return (
            <span>
              {t('hourlyAbsences.partialCovered', {
                covered: formatNumber(hoursCoveredTotal),
                total: formatNumber(hoursRequested),
              })}
              {dateFormatted ? ` (${dateFormatted})` : ''}
            </span>
          );
        }

        return (
          <span>
            {t('hourlyAbsences.yes')}
            {dateFormatted ? ` (${dateFormatted})` : ''}
          </span>
        );
      },
    },
    {
      Header: t('hourlyAbsences.comment'),
      accessor: 'comment',
      Cell: ({ cell }) => <span>{cell.value}</span>,
    },
    {
      Header: t('hourlyAbsences.actions'),
      accessor: 'actions',
      width: '10%',
      Cell: ({ row }) => {
        const { buttons, id } = row.original;
        return <div>{renderActions(buttons, id)}</div>;
      },
    },
  ];

  return (
    <InnerWrapper isLoading={isLoading}>
      <FetchTable
        loading={isLoading}
        entityName="hourly-absences"
        entityEndpoint="hourly-absences"
        hasPermission={[PermissionKeys.CAN_MANAGE_HOURLY_ABSENCES]}
        columns={columns}
        filters={tableFilters}
        hideSearch
        pageLimit={10}
        pagination
        {...(!isOperator && {
          onAdd: createHourlyAbsences,
          addBtnLabel: t('hourlyAbsences.create'),
        })}
      />
    </InnerWrapper>
  );
};

export default HourlyAbsencesScreen;
